import React, { useRef } from "react";
import { ProgressBar } from "primereact/progressbar";
import PropTypes from "prop-types";
import "./conversations.scss";
import AttachmentIcon from "./AttachmentIcon";
import CustomeButton from "../../Button";
import ErrorMessage from "./ErrorMessage";
import { FileUpload } from "primereact/fileupload";
import {
  openFileInNewTab,
  getDocumentLabel,
  formatFileSize,
} from "~/utils/fileUtils";
import DocumentDescription from "~/components/document-description";

const Attachments = ({
  file,
  fileHandler,
  progressBar,
  icon,
  error = false,
  handleErrorVisibility = () => {},
  getFileSizeMB = () => {},
  handleReplace = () => {},
  showReplaceButton = false,
  showPreviewIcon = true,
  showDescriptionIcon = false,
  id,
  relatedType = "",
  displayName = "",
  useUploadActions = false,
}) => {
  const replaceInErrorRef = useRef(null);
  const replaceButtonRef = useRef(null);

  const baseName =
    displayName || file?.metadata?.display_name || file?.fileName || file?.name;
  const fileNameToDisplay =
    displayName || file?.metadata?.display_name || file?.fileName || file?.name;
  const fileType = getDocumentLabel(fileNameToDisplay);
  const fileSize = formatFileSize(file?.size || 0);

  const onReplaceSelect = (e, isErrorBlock) => {
    const selectedFile = e?.files?.[0];
    if (!selectedFile) return;
    handleReplace(e, file);
    const ref = isErrorBlock ? replaceInErrorRef : replaceButtonRef;
    setTimeout(() => ref.current?.clear?.(), 0);
  };

  return (
    <div className="attachment">
      <div
        className={`attachment__file ${showPreviewIcon ? "gap-3 pr-4" : ""}`}
      >
        <div className="file-content">
          <div className="icon-container">
            <AttachmentIcon file={file} />
          </div>
          <div className="file-details">
            <p
              className={`file-name ${showPreviewIcon ? "file-name-link" : ""}`}
              onClick={() => {
                showPreviewIcon && openFileInNewTab(file);
              }}
            >{`${fileNameToDisplay}`}</p>
            <div className="flex gap-2 text- text-color-secondary">
              <span>{fileType}.</span>
              <span>File Size:</span>
              <span>{fileSize}</span>
            </div>
            <div></div>
            {progressBar && (
              <ProgressBar
                value={40}
                mode="indeterminate"
                color="#188A42"
                showValue={false}
              ></ProgressBar>
            )}
          </div>
        </div>
        {error.state && (
          <div className="attachment__file--actions">
            <CustomeButton
              label="Remove"
              variant="remove"
              className="access-btn"
              onClick={fileHandler}
            />
            {getFileSizeMB(file) < 20 && (
              <div className="replase__btn">
                <FileUpload
                  ref={replaceInErrorRef}
                  name="replace-file-error"
                  customUpload={true}
                  onSelect={(e) => onReplaceSelect(e, true)}
                  chooseOptions={{
                    label: "Replace",
                    className: "attachment-button  p-button-outlined ",
                  }}
                  mode="advanced"
                />
              </div>
            )}
          </div>
        )}
        {!error.state &&
          (useUploadActions ? (
            <div className="attachment__file-actions">
              {showPreviewIcon && (
                <button
                  type="button"
                  className="attachment-action-button"
                  onClick={() => openFileInNewTab(file)}
                >
                  <i className="pi pi-external-link action-icon" />
                </button>
              )}
              <button
                type="button"
                className="attachment-action-button"
                onClick={fileHandler}
              >
                <i className="pi pi-trash action-icon" />
              </button>
            </div>
          ) : (
            <div className="flex align-items-center justify-content-center gap-2 ml-2">
              {showDescriptionIcon && (
                <DocumentDescription data={file} id={id} type={relatedType} />
              )}
              {showPreviewIcon && (
                <i
                  className="pi pi-external-link action-icon"
                  onClick={() => openFileInNewTab(file)}
                />
              )}
              <i className={`pi ${icon} action-icon`} onClick={fileHandler} />
            </div>
          ))}
      </div>
      {!error.state && showReplaceButton && handleReplace && (
        <div className="mt-2">
          <FileUpload
            ref={replaceButtonRef}
            name="replace-file-main"
            customUpload={true}
            onSelect={(e) => onReplaceSelect(e, false)}
            chooseOptions={{
              label: "Replace File",
              className: "select-file-btn",
              icon: "pi pi-upload",
              style: {
                color: "#000000",
                borderColor: "#188A42",
                backgroundColor: "transparent",
              },
            }}
            mode="basic"
            auto={true}
          />
        </div>
      )}
      {error.visible && error.state && (
        <ErrorMessage
          summary={
            getFileSizeMB(file) > 20
              ? "File Exceeds Size Limit"
              : "Unsupported File"
          }
          detail={
            getFileSizeMB(file) > 20
              ? "This file exceeds the maximum size limit. Please upload a smaller file"
              : "This file type is not supported. Please upload a valid file format."
          }
          handleClose={handleErrorVisibility}
        />
      )}
    </div>
  );
};

Attachments.propTypes = {
  file: PropTypes.object,
  fileHandler: PropTypes.func,
  progressBar: PropTypes.bool,
  icon: PropTypes.string,
  error: PropTypes.object,
  handleErrorVisibility: PropTypes.func,
  getFileSizeMB: PropTypes.func,
  handleReplace: PropTypes.func,
  showReplaceButton: PropTypes.bool,
  showPreviewIcon: PropTypes.bool,
  showDescriptionIcon: PropTypes.bool,
  id: PropTypes.string,
  relatedType: PropTypes.string,
  displayName: PropTypes.string,
  useUploadActions: PropTypes.bool,
};

export default Attachments;
